import { useCallback, useEffect, useState } from 'react';
import { getCancellationProgress, LEDGER_CHANGED_EVENT } from '@/services/deals.service.js';
import ConfirmCancellationInline from './ConfirmCancellationInline.jsx';
import CarrierDocAttach, { ledgerRowName } from './CarrierDocAttach.jsx';

// A cancellation in progress (deals-door-1 round 2, David 2026-10-28): the deal keeps its stage (Bound) until a signed LPR and
// the carrier's cancellation endorsement are both on file, and moves to Cancelled by itself when the second one lands (round 3:
// on its effective date when that is later). Until then this line says so in words, naming only what is missing. The server derives the sentence (utils/cancellation.js); the
// page derives nothing. A read the server could not make says that it could not check, never that nothing is missing.
export default function DealCancellationNote({ dealId }) {
  const [state, setState] = useState(null);
  const [tick, setTick] = useState(0);
  const reread = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    getCancellationProgress(dealId)
      .then((d) => { if (alive) setState(d || null); })
      .catch(() => { if (alive) setState({ unchecked: true }); });
    return () => { alive = false; };
  }, [dealId, tick]);
  // A ledger change anywhere on the page (this note's own buttons, the Transaction history rows) re-reads the note.
  useEffect(() => {
    const on = (e) => { if (String(e?.detail?.dealId) === String(dealId)) reread(); };
    window.addEventListener(LEDGER_CHANGED_EVENT, on);
    return () => window.removeEventListener(LEDGER_CHANGED_EVENT, on);
  }, [dealId, reread]);
  if (!state) return null;
  if (state.unchecked) {
    return <p className="mt-2 text-xs text-text-muted" data-cancellation-progress="unchecked">Cancellation documents: could not check.</p>;
  }
  // Round 3: in progress, scheduled for its date (R2k-Q2), or the grandfathered legacy Cancelled (R2k-Q4); and the to-dos the
  // server names (R2k-Q3: the carrier's endorsement file not on file; paperwork-1 1b: read from the documents row linked to the
  // governing cancellation, which the route answers as carrier_document with its derived name), each in words, never a gate.
  const todos = Array.isArray(state.todos) ? state.todos : [];
  if (!state.sentence && !todos.length) return null;
  // The note carries its own buttons (David 2026-10-29), named by the server (utils/cancellation cancellationNoteActions)
  // against the row it governs; the same doors as that row in Transaction history.
  const actions = Array.isArray(state.actions) && state.transaction_id ? state.actions : [];
  const rowName = ledgerRowName('cancellation', state.effective_date);
  return (
    <div className="mt-2 space-y-1">
      {state.sentence ? (
        <p className="rounded-card border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-xs font-medium text-text" data-cancellation-progress={state.state || 'in_progress'}>
          {state.sentence}
        </p>
      ) : null}
      {todos.map((t) => (
        <p key={t} className="text-xs text-text-muted" data-cancellation-todo="true">{t}</p>
      ))}
      {actions.length ? (
        <div className="flex flex-wrap items-start gap-2" data-cancellation-actions="true">
          {actions.includes('enter_return_premium') ? (
            <ConfirmCancellationInline dealId={dealId} txn={{ id: state.transaction_id, effective_date: state.effective_date }} label={`Carrier confirmed: enter return premium for ${rowName}`} onConfirmed={reread} />
          ) : null}
          {actions.includes('attach_carrier_doc') ? (
            <CarrierDocAttach dealId={dealId} txnId={state.transaction_id} document={state.carrier_document || null} rowName={rowName} onAttached={reread} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
